import './AppShell.scss'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { MdOutlineMenu, MdOutlineMenuOpen } from 'react-icons/md'
import { useLocation } from 'react-router-dom'
import { clx, IconButton } from '../../ui'
import { ActivityLine } from '../ActivityLine'
import { Breadcrumbs } from '../Breadcrumbs'
import { EnvBanner } from '../EnvBanner'
import type { NavItem } from '../nav'
import { NavDrawer } from '../NavDrawer'
import { Sidebar, useRailExpanded, useSidebarExpanded } from '../Sidebar'
import { Topbar } from '../Topbar'
import { useNavMode, type NavMode } from './useNavMode'

const NAV_ID = 'app-shell-nav'

export interface AppShellProps {
  /** Navigation, already filtered by RBAC and resolved for unported areas. */
  nav: NavItem[]
  version: string
  user?: { name: string } | null | undefined
  onLogout: () => void
  /** Top bar Refresh. Defaults to invalidating every query. */
  onRefresh?: () => void
  /** Defaults to `<EnvBanner version={version} />`; `null` hides it. */
  envBanner?: ReactNode
  /** Defaults to `<Breadcrumbs />` (needs a data router); `null` hides it. */
  breadcrumbs?: ReactNode
  /** Force a navigation form; `auto` follows the viewport (ADR 0012). */
  mode?: 'auto' | NavMode
  /**
   * Where the collapse toggle of the sidebar and rail sits: at the start of
   * the top bar (the default), or at the top of the sidebar itself.
   */
  sidebarToggle?: 'topbar' | 'sidebar'
  /** The page (the integrator's `<Outlet />`). */
  children?: ReactNode
}

/**
 * The full-viewport grid (ADR 0012): header row, then a row with the
 * navigation and `main`. `main` is the single page scroller.
 *
 * `--app-header-height` is measured onto the root for anything positioned
 * against the viewport. Inside `main` it is reset to 0: main is the
 * scroller, so a sticky header there sticks to main's top edge, which
 * already sits below the top bar (DataTable reads
 * `inset-block-start: var(--app-header-height, 0px)`).
 *
 * A page root with `data-shell-fill` gets main's height from `tablet`, so a
 * list's TableFrame can scroll its rows itself (ADR 0025).
 */
export function AppShell({
  nav,
  version,
  user,
  onLogout,
  onRefresh,
  envBanner,
  breadcrumbs,
  mode: forcedMode = 'auto',
  sidebarToggle = 'topbar',
  children
}: AppShellProps) {
  const mode = useNavMode(forcedMode)
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const rootRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)
  const mainRef = useRef<HTMLElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  // Owned here so the top bar's toggle and the sidebar share one state.
  const rail = useRailExpanded()
  const docked = useSidebarExpanded()
  const [isNavExpanded, setNavExpanded] = mode === 'rail' ? rail : docked

  // The drawer is open only for the path it was opened on, and only in
  // drawer mode: navigating or widening the window closes it.
  const [drawer, setDrawer] = useState({ isOpen: false, pathname })
  const isDrawerOpen = mode === 'drawer' && drawer.isOpen && drawer.pathname === pathname

  // Focus goes back to the menu button after the drawer's layer has released
  // the background (useModalLayer restores focus before removing `inert`).
  const wasDrawerOpen = useRef(false)
  useEffect(() => {
    if (isDrawerOpen) {
      wasDrawerOpen.current = true
      return
    }
    if (!wasDrawerOpen.current) return
    wasDrawerOpen.current = false
    menuButtonRef.current?.focus()
  }, [isDrawerOpen])

  useLayoutEffect(() => {
    const root = rootRef.current
    const header = headerRef.current
    if (!root || !header || typeof ResizeObserver === 'undefined') return
    const publish = () => {
      root.style.setProperty('--app-header-height', `${header.offsetHeight}px`)
    }
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(header)
    return () => {
      observer.disconnect()
    }
  }, [])

  // One scroller: a new page starts at its top.
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0
  }, [pathname])

  const refresh =
    onRefresh ??
    (() => {
      void queryClient.invalidateQueries()
    })

  return (
    <div
      ref={rootRef}
      className={clx('app-shell', `app-shell--as-${mode}`)}
      data-nav={mode === 'drawer' ? undefined : isNavExpanded ? 'expanded' : 'collapsed'}
    >
      <a
        href="#main"
        className="app-shell__skip"
        onClick={(event) => {
          event.preventDefault()
          mainRef.current?.focus()
        }}
      >
        Skip to content
      </a>
      <ActivityLine className="app-shell__activity" />
      <Topbar
        ref={headerRef}
        user={user}
        onLogout={onLogout}
        onRefresh={refresh}
        envBanner={envBanner === undefined ? <EnvBanner version={version} /> : envBanner}
        menuButton={
          mode === 'drawer' ? (
            <IconButton
              ref={menuButtonRef}
              label="Open navigation"
              icon={<MdOutlineMenu />}
              aria-haspopup="dialog"
              aria-expanded={isDrawerOpen}
              hasTooltip={false}
              onClick={() => {
                setDrawer({ isOpen: true, pathname })
              }}
            />
          ) : (
            sidebarToggle === 'topbar' && (
              <IconButton
                label={isNavExpanded ? 'Collapse navigation' : 'Expand navigation'}
                icon={isNavExpanded ? <MdOutlineMenuOpen /> : <MdOutlineMenu />}
                aria-expanded={isNavExpanded}
                aria-controls={NAV_ID}
                hasTooltip={false}
                onClick={() => {
                  setNavExpanded(!isNavExpanded)
                }}
              />
            )
          )
        }
      />
      <div className="app-shell__body">
        {mode !== 'drawer' && (
          <Sidebar
            id={NAV_ID}
            items={nav}
            version={version}
            form={mode}
            isExpanded={isNavExpanded}
            onExpandedChange={setNavExpanded}
            hasToggle={sidebarToggle === 'sidebar'}
          />
        )}
        <main
          ref={mainRef}
          id="main"
          tabIndex={-1}
          aria-label="Content"
          className="app-shell__main"
        >
          {breadcrumbs === undefined ? <Breadcrumbs /> : breadcrumbs}
          {children}
        </main>
      </div>
      {mode === 'drawer' && (
        <NavDrawer
          isOpen={isDrawerOpen}
          onClose={() => {
            setDrawer({ isOpen: false, pathname })
          }}
        >
          <Sidebar items={nav} version={version} form="drawer" />
        </NavDrawer>
      )}
    </div>
  )
}
